const defaultMaxEdge = 1600;
const defaultJpegQuality = 0.8;

/**
 * Normalises phone photos to a document-safe, mobile-data-friendly JPEG.
 */
export function reencodeImageToJpeg(
	file: File,
	maxEdge: number = defaultMaxEdge,
	quality: number = defaultJpegQuality
): Promise<Blob> {
	return readAsDataUrl(file).then(dataUrl => loadImage(dataUrl)).then(image => {
		const dimensions = constrainedDimensions(image.naturalWidth || image.width, image.naturalHeight || image.height, maxEdge);
		const canvas = document.createElement("canvas");
		canvas.width = dimensions.width;
		canvas.height = dimensions.height;
		const context = canvas.getContext("2d");
		if (!context) {
			throw new Error("Your browser could not prepare this photo.");
		}
		context.drawImage(image, 0, 0, dimensions.width, dimensions.height);
		return canvasToBlob(canvas, "image/jpeg", quality);
	});
}

export function attachmentFileName(prefix: string, registration: string, extension: "jpg" | "png"): string {
	const safeRegistration = registration.toUpperCase().replace(/[^A-Z0-9]/g, "") || "vehicle";
	return `${prefix}${safeRegistration}.${extension}`;
}

function constrainedDimensions(width: number, height: number, maxEdge: number): {width: number; height: number} {
	if (!width || !height) {
		throw new Error("This image could not be read.");
	}
	const scale = Math.min(1, maxEdge / Math.max(width, height));
	return {
		width: Math.max(1, Math.round(width * scale)),
		height: Math.max(1, Math.round(height * scale))
	};
}

function readAsDataUrl(file: File): Promise<string> {
	return new Promise<string>((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = () => reject(new Error("This image could not be read."));
		reader.readAsDataURL(file);
	});
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
	return new Promise<HTMLImageElement>((resolve, reject) => {
		const image = new Image();
		image.onload = () => resolve(image);
		image.onerror = () => reject(new Error("This photo format is not supported by your browser."));
		image.src = dataUrl;
	});
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
	return new Promise<Blob>((resolve, reject) => {
		canvas.toBlob(blob => {
			if (blob) resolve(blob);
			else reject(new Error("Your browser could not prepare this photo."));
		}, type, quality);
	});
}
